export type DeviceCategory = 'mobile' | 'tablet' | 'desktop';
export type OSName = 'iOS' | 'Android' | 'Windows' | 'macOS' | 'Linux' | 'ChromeOS' | 'Unknown';

export type DeviceInfo = {
  device_category: DeviceCategory;
  os: OSName;
  os_version?: string;       // e.g., "17.5" or "14" or "10.15.7"
  vp_w: number;
  vp_h: number;
  dpr: number;
  touch: boolean;
};

export function getDeviceCategory(): DeviceCategory {
  const ua = navigator.userAgent || '';
  const isIPad =
    /\biPad\b/i.test(ua) ||
    (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1); // iPadOS 13+

  const isIPhone = /\biPhone\b/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isAndroidMobile = isAndroid && /Mobile/i.test(ua);
  const isAndroidTablet = isAndroid && !/Mobile/i.test(ua);
  const isMobile = isIPhone || isAndroidMobile || /Mobi/i.test(ua);
  const isTablet = isIPad || isAndroidTablet;

  if (!isMobile && !isTablet) {
    const w = window.innerWidth || document.documentElement.clientWidth || 0;
    if (w < 768) return 'mobile';
    if (w < 1024) return 'tablet';
  }
  return isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop';
}

export function getOS(): { os: OSName; os_version?: string } {
  const ua = navigator.userAgent || '';
  const uaLC = ua.toLowerCase();

  // Chrome UA-CH (optional, low entropy)
  const uaData = (navigator as any).userAgentData;
  const plat = (uaData?.platform || navigator.platform || '').toLowerCase();

  // iPadOS masquerades as macOS; detect via touch-capable Mac
  const isIPadOS = navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1;

  // Android
  if (uaLC.includes('android')) {
    const m = ua.match(/Android\s([0-9._]+)/i);
    return { os: 'Android', os_version: m?.[1]?.replace(/_/g, '.') };
  }

  // iOS & iPadOS
  if (/iphone|ipad|ipod/i.test(ua) || isIPadOS) {
    const m = ua.match(/OS\s([0-9_]+)/i); // iOS 17_5, etc.
    return { os: 'iOS', os_version: m?.[1]?.replace(/_/g, '.') };
  }

  // Windows
  if (plat.includes('win') || uaLC.includes('windows')) {
    const m = ua.match(/Windows NT\s([0-9.]+)/i); // 10.0, 6.3, etc.
    return { os: 'Windows', os_version: m?.[1] };
  }
  
  // ChromeOS
  if (uaLC.includes('cros')) {
    return { os: 'ChromeOS' };
  }

  // Linux
  if (plat.includes('linux') || uaLC.includes('linux')) {
    return { os: 'Linux' };
  }
  
  // macOS (exclude iPadOS case above)
  if (plat.includes('mac') || uaLC.includes('mac os x')) {
    const m = ua.match(/Mac OS X\s([0-9_.]+)/i);
    return { os: 'macOS', os_version: m?.[1]?.replace(/_/g, '.') };
  }


  return { os: 'Unknown' };
}

export function getDeviceInfo(): DeviceInfo {
  const { os, os_version } = getOS();
  return {
    device_category: getDeviceCategory(),
    os,
    os_version,
    vp_w: window.innerWidth || 0,
    vp_h: window.innerHeight || 0,
    dpr: Math.round((window.devicePixelRatio || 1) * 100) / 100,
    touch: 'ontouchstart' in window,
  };
}
